import { useEffect, useState, type FormEvent } from 'react';
import { Bookmark, Copy, Eye, EyeOff } from 'lucide-react';
import { ConfirmDeleteButton } from './confirm-delete-button';
import { toast } from 'sonner';
import {
  fetchBookmarklet,
  fetchDecryptedCredential,
  useCreateCredential,
  useDeleteCredential,
  useUpdateCredential,
} from '../lib/hooks';
import { Modal } from './modal';

type Props = {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
  workspaceName: string;
  workspaceUrl: string;
  existingCredentialId: string | null;
};

/**
 * Modal cho 1 workspace tile:
 * - Nếu chưa có credential → form add (label, username, password, notes)
 * - Nếu đã có → form edit + nút Decrypt/Copy/Open/Delete + Bookmarklet
 */
export function CredentialModal({
  open,
  onClose,
  workspaceId,
  workspaceName,
  workspaceUrl,
  existingCredentialId,
}: Props) {
  const isEdit = Boolean(existingCredentialId);
  const create = useCreateCredential();
  const update = useUpdateCredential();
  const del = useDeleteCredential();

  const [label, setLabel] = useState(`${workspaceName} login`);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [notes, setNotes] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [hasFetchedExisting, setHasFetchedExisting] = useState(false);

  // When opening edit mode, fetch decrypted current values once
  useEffect(() => {
    if (!open) return;
    if (!isEdit || hasFetchedExisting) return;
    (async () => {
      try {
        const data = await fetchDecryptedCredential(existingCredentialId!);
        setUsername(data.username);
        setPassword(data.password);
        setNotes(data.notes ?? '');
        setHasFetchedExisting(true);
      } catch {
        toast.error('Không decrypt được credential');
      }
    })();
  }, [open, isEdit, existingCredentialId, hasFetchedExisting]);

  const reset = () => {
    setLabel(`${workspaceName} login`);
    setUsername('');
    setPassword('');
    setNotes('');
    setShowPassword(false);
    setHasFetchedExisting(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!username.trim() && !password.trim()) {
      toast.error('Cần ít nhất username hoặc password');
      return;
    }
    try {
      if (isEdit && existingCredentialId) {
        await update.mutateAsync({
          id: existingCredentialId,
          label: label.trim(),
          payload: {
            username: username.trim(),
            password,
            notes: notes.trim() || undefined,
          },
        });
        toast.success('Đã cập nhật credential');
      } else {
        await create.mutateAsync({
          label: label.trim(),
          workspaceId,
          payload: {
            username: username.trim(),
            password,
            notes: notes.trim() || undefined,
          },
        });
        toast.success('Đã lưu credential — encrypted AES-256-GCM');
      }
      handleClose();
    } catch {
      toast.error('Lỗi khi lưu');
    }
  };

  const handleCopyPassword = async () => {
    try {
      const data = existingCredentialId
        ? await fetchDecryptedCredential(existingCredentialId)
        : { password };
      await navigator.clipboard.writeText(data.password);
      toast.success('Đã copy password vào clipboard (sẽ xoá sau 30s)');
      setTimeout(() => navigator.clipboard.writeText('').catch(() => {}), 30_000);
    } catch {
      toast.error('Không copy được');
    }
  };

  const handleOpenWithCopy = async () => {
    if (!existingCredentialId) return;
    try {
      const data = await fetchDecryptedCredential(existingCredentialId);
      await navigator.clipboard.writeText(data.password);
      window.open(workspaceUrl, '_blank', 'noopener');
      toast.success('Đã copy password — paste vào form login (clipboard xoá sau 30s)');
      setTimeout(() => navigator.clipboard.writeText('').catch(() => {}), 30_000);
      handleClose();
    } catch {
      toast.error('Không mở được');
    }
  };

  const handleGetBookmarklet = async () => {
    if (!existingCredentialId) return;
    try {
      const { bookmarkletUrl, instructions } = await fetchBookmarklet(existingCredentialId);
      // Open new window with the bookmarklet text — user drags to bookmarks bar
      const win = window.open('', '_blank', 'width=600,height=400');
      if (!win) {
        toast.error('Browser block popup. Cho phép popup site này.');
        return;
      }
      win.document.write(`
<!doctype html>
<html><head><title>MyOS Bookmarklet — ${workspaceName}</title>
<style>
  body{font-family:system-ui;padding:24px;background:#0b0f1a;color:#e2e8f0;}
  h1{font-size:18px;margin:0 0 8px;}
  p{font-size:13px;color:#94a3b8;line-height:1.5;}
  a.bm{display:inline-block;padding:10px 16px;background:linear-gradient(to bottom right,#6366f1,#d946ef);
       color:white;text-decoration:none;border-radius:8px;font-weight:600;margin:16px 0;cursor:grab;}
  code{background:#1e293b;padding:2px 6px;border-radius:4px;font-size:12px;}
</style></head><body>
  <h1>🔖 MyOS Login — ${workspaceName}</h1>
  <p>${instructions}</p>
  <a class="bm" href="${bookmarkletUrl}">⚡ MyOS Login → ${workspaceName}</a>
  <p>Kéo nút trên (⚡) lên Bookmarks bar trong Chrome.<br>
     Khi đến trang login của <code>${workspaceUrl}</code>, click bookmark → form auto-fill.</p>
  <p style="color:#fbbf24;">⚠️ Bookmark KHÔNG chứa password.
     Mỗi lần click, nó sẽ fetch credential từ MyOS (cần đăng nhập MyOS cùng browser).</p>
</body></html>
      `);
      win.document.close();
    } catch {
      toast.error('Không lấy được bookmarklet');
    }
  };

  const handleDelete = async () => {
    if (!existingCredentialId) return;
    try {
      await del.mutateAsync(existingCredentialId);
      toast.success('Đã xoá credential');
      handleClose();
    } catch {
      toast.error('Không xoá được');
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={isEdit ? `🔒 Credential — ${workspaceName}` : `🔒 Thêm credential — ${workspaceName}`}
      footer={
        <div className="flex w-full items-center justify-between">
          <div className="flex gap-2">
            {isEdit && (
              <ConfirmDeleteButton
                size="md"
                label="Xoá credential"
                disabled={del.isPending}
                onConfirm={handleDelete}
              />
            )}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={handleClose} className="btn-secondary">
              Huỷ
            </button>
            <button
              type="submit"
              form="cred-form"
              className="btn-primary"
              disabled={create.isPending || update.isPending}
            >
              {create.isPending || update.isPending ? '…' : 'Lưu'}
            </button>
          </div>
        </div>
      }
    >
      <form id="cred-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="label">Label hiển thị</label>
          <input
            className="input"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            required
          />
        </div>
        <div>
          <label className="label">Username / Email</label>
          <input
            className="input font-mono text-xs"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="admin@example.com"
            autoComplete="off"
          />
        </div>
        <div>
          <label className="label">Password</label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              className="input pr-10 font-mono text-xs"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="absolute top-1/2 right-2 -translate-y-1/2 rounded p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-white/10"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <div>
          <label className="label">Notes (optional)</label>
          <textarea
            className="input min-h-[60px] text-xs"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="2FA backup codes, hint, etc."
          />
        </div>

        {isEdit && (
          <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-3 dark:border-white/5">
            <button type="button" onClick={handleOpenWithCopy} className="btn-secondary text-xs">
              <Copy className="h-3.5 w-3.5" />
              Mở site + copy password
            </button>
            <button type="button" onClick={handleCopyPassword} className="btn-secondary text-xs">
              <Copy className="h-3.5 w-3.5" />
              Copy password
            </button>
            <button type="button" onClick={handleGetBookmarklet} className="btn-secondary text-xs">
              <Bookmark className="h-3.5 w-3.5" />
              Bookmarklet auto-fill
            </button>
          </div>
        )}

        <p className="border-t border-slate-200 pt-3 text-[11px] text-slate-500 dark:border-white/5">
          🔐 Encrypted AES-256-GCM với master key trên server. Chỉ truyền plaintext khi click
          "Decrypt".
        </p>
      </form>
    </Modal>
  );
}
